import React, { useState, useEffect } from 'react';
import { Project, SectionId } from '../types';
import { FadeIn } from './ui/FadeIn';
import { Play } from 'lucide-react';
import { VideoModal } from './ui/VideoModal';

// Commercial/Lifestyle Reels - 9:16 Vertical Format
const commercialReels: Project[] = [
  {
    id: 1,
    title: "Chic & Scoop",
    location: "Brand Story",
    category: "Lifestyle",
    image: "",
    videoId: "1139758620",
    videoPlatform: "vimeo"
  },
  {
    id: 2,
    title: "Brand Story",
    location: "Commercial",
    category: "Commercial",
    image: "",
    videoId: "1139758685",
    videoPlatform: "vimeo"
  },
  {
    id: 3,
    title: "Fashion Edit",
    location: "Editorial",
    category: "Fashion",
    image: "",
    videoId: "1139758746",
    videoPlatform: "vimeo"
  }
];

export const Reels: React.FC = () => {
  const [reels, setReels] = useState<Project[]>(commercialReels);
  const [selectedReel, setSelectedReel] = useState<Project | null>(null);
  const [loadingThumbnails, setLoadingThumbnails] = useState(true);

  // Fetch Vimeo Thumbnails
  useEffect(() => {
    const fetchThumbnails = async () => {
      setLoadingThumbnails(true);
      const updatedReels = await Promise.all(
        commercialReels.map(async (reel) => {
          if (reel.videoPlatform === 'vimeo' && reel.videoId) {
            try {
              const response = await fetch(
                `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${reel.videoId}&width=1080`
              );
              const data = await response.json();
              if (data.thumbnail_url) {
                // Get highest res thumbnail
                let highResThumb = data.thumbnail_url;
                highResThumb = highResThumb.replace(/_\d+x\d+/, '_1080x1920');
                highResThumb = highResThumb.replace(/-d_\d+/, '-d_1080');
                return { ...reel, image: highResThumb };
              }
            } catch (error) {
              console.error(`Failed to fetch thumbnail for ${reel.title}`, error);
            }
          }
          return reel;
        })
      );
      setReels(updatedReels);
      setLoadingThumbnails(false);
    };

    fetchThumbnails();
  }, []);

  const handleReelClick = (reel: Project) => {
    if (reel.videoId) {
      setSelectedReel(reel);
    }
  };

  return (
    <>
      <section id="reels" className="py-24 md:py-32 bg-stone-900">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeIn className="mb-16 text-center">
            <p className="text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-4">Commercial Work</p>
            <h2 className="text-4xl md:text-5xl font-serif text-stone-100 mb-4">Reels & Shorts</h2>
            <div className="w-12 h-[1px] bg-stone-700 mx-auto"></div>
          </FadeIn>

          {/* 9:16 Vertical Reels Grid */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-10">
            {reels.map((reel, index) => (
              <FadeIn key={reel.id} delay={index * 150}>
                <div 
                  className="group cursor-pointer w-[280px] sm:w-[300px] md:w-[320px]"
                  onClick={() => handleReelClick(reel)}
                >
                  {/* 9:16 Aspect Ratio Container */}
                  <div className="relative overflow-hidden aspect-[9/16] bg-stone-800 rounded-lg shadow-2xl">
                    {/* Loading State */}
                    {loadingThumbnails && (
                      <div className="absolute inset-0 bg-stone-800 animate-pulse flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-stone-600 border-t-stone-400 rounded-full animate-spin" />
                      </div>
                    )}
                    
                    {/* Thumbnail Image */}
                    {reel.image && (
                      <img 
                        src={reel.image} 
                        alt={reel.title}
                        className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105 group-hover:brightness-110"
                        loading="lazy"
                      />
                    )}
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 opacity-70 group-hover:opacity-50 transition-opacity duration-500" />
                    
                    {/* Play Button */}
                    <div className="absolute inset-0 flex items-center justify-center z-20">
                      <div className="bg-white/10 backdrop-blur-md p-4 rounded-full border border-white/30 transform scale-100 group-hover:scale-110 group-hover:bg-white/20 transition-all duration-500 shadow-xl">
                        <Play size={24} className="text-white ml-0.5" fill="white" strokeWidth={0} />
                      </div>
                    </div>
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4 z-20">
                      <span className="text-[9px] uppercase tracking-widest text-white/80 bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/20">
                        {reel.category}
                      </span>
                    </div>
                    
                    {/* Title at Bottom */}
                    <div className="absolute bottom-0 left-0 right-0 p-5 z-20">
                      <p className="text-[9px] tracking-[0.2em] uppercase text-white/50 mb-1">
                        {reel.location}
                      </p>
                      <h3 className="text-xl font-serif text-white">
                        {reel.title}
                      </h3>
                    </div>
                    
                    {/* Reel Indicator - Bottom Right */}
                    <div className="absolute bottom-5 right-5 z-20">
                      <svg 
                        className="w-5 h-5 text-white/60" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="1.5"
                      >
                        <rect x="2" y="2" width="20" height="20" rx="5" />
                        <circle cx="12" cy="12" r="4" />
                        <circle cx="6" cy="6" r="1.5" fill="currentColor" />
                        <circle cx="18" cy="6" r="1.5" fill="currentColor" />
                        <circle cx="6" cy="18" r="1.5" fill="currentColor" />
                        <circle cx="18" cy="18" r="1.5" fill="currentColor" />
                      </svg>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* Optional: View More Button */}
          <FadeIn className="mt-16 text-center">
            <p className="text-stone-500 text-sm italic font-serif">
              More commercial work coming soon
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Video Modal */}
      <VideoModal 
        isOpen={!!selectedReel} 
        onClose={() => setSelectedReel(null)} 
        videoId={selectedReel?.videoId || ''}
        platform={selectedReel?.videoPlatform || 'vimeo'}
        title={selectedReel?.title || ''}
      />
    </>
  );
};
